/* ==========================================================================
   Nest — theme behaviour
   Plain custom elements, no framework, no build step.
   ========================================================================== */
(function () {
  'use strict';

  var Theme = window.Theme || {};
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ----------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */
  function formatMoney(cents) {
    var amount = (cents / 100).toFixed(2);
    var format = Theme.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*\w+\s*\}\}/, amount);
  }

  function lockScroll(locked) {
    document.body.classList.toggle('is-locked', locked);
  }

  function trapFocus(container, event) {
    var nodes = Array.prototype.slice.call(container.querySelectorAll(FOCUSABLE))
      .filter(function (el) { return el.offsetParent !== null; });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function announce(message) {
    var region = document.getElementById('ThemeLiveRegion');
    if (!region) {
      region = document.createElement('p');
      region.id = 'ThemeLiveRegion';
      region.setAttribute('role', 'status');
      region.setAttribute('aria-live', 'polite');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
    }
    region.textContent = message;
  }

  /* ----------------------------------------------------------------------
     Drawer base
     ---------------------------------------------------------------------- */
  var DrawerBase = (function () {
    function Drawer(el) {
      this.el = el;
      this.opener = null;
      this.onKeydown = this.onKeydown.bind(this);
      var self = this;
      el.querySelectorAll('[data-drawer-close]').forEach(function (btn) {
        btn.addEventListener('click', function () { self.close(); });
      });
    }

    Drawer.prototype.open = function (opener) {
      this.opener = opener || null;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      lockScroll(true);
      document.addEventListener('keydown', this.onKeydown);
      var panel = this.el.querySelector('.drawer__panel');
      var focusTarget = panel && panel.querySelector(FOCUSABLE);
      if (focusTarget) window.setTimeout(function () { focusTarget.focus(); }, 60);
    };

    Drawer.prototype.close = function () {
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      document.removeEventListener('keydown', this.onKeydown);
      if (this.opener && document.body.contains(this.opener)) this.opener.focus();
    };

    Drawer.prototype.onKeydown = function (event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
      } else if (event.key === 'Tab') {
        var panel = this.el.querySelector('.drawer__panel');
        if (panel) trapFocus(panel, event);
      }
    };

    return Drawer;
  })();

  /* ----------------------------------------------------------------------
     <cart-drawer>
     ---------------------------------------------------------------------- */
  class CartDrawerElement extends HTMLElement {
    connectedCallback() {
      this.drawer = new DrawerBase(this);
    }
    open(opener) { if (this.drawer) this.drawer.open(opener); }
    close() { if (this.drawer) this.drawer.close(); }
  }
  customElements.define('cart-drawer', CartDrawerElement);

  class MenuDrawerElement extends HTMLElement {
    connectedCallback() {
      this.drawer = new DrawerBase(this);
    }
    open(opener) { if (this.drawer) this.drawer.open(opener); }
    close() { if (this.drawer) this.drawer.close(); }
  }
  customElements.define('menu-drawer', MenuDrawerElement);

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('[data-drawer-open]');
    if (!trigger) return;
    var target = document.querySelector(trigger.getAttribute('data-drawer-open'));
    if (!target || typeof target.open !== 'function') return;
    event.preventDefault();
    target.open(trigger);
  });

  /* ----------------------------------------------------------------------
     Cart operations
     ---------------------------------------------------------------------- */
  var Cart = {
    sectionsToRender: function () {
      var ids = [];
      if (document.getElementById('CartDrawer')) ids.push('cart-drawer');
      if (document.querySelector('[data-section-type="main-cart"]')) ids.push('main-cart');
      if (document.querySelector('[data-cart-icon-bubble]')) ids.push('header');
      return ids;
    },

    renderSections: function (sections) {
      if (!sections) return;
      Object.keys(sections).forEach(function (key) {
        var markup = sections[key];
        if (!markup) return;
        var parsed = new DOMParser().parseFromString(markup, 'text/html');

        if (key === 'cart-drawer') {
          var current = document.getElementById('CartDrawer');
          var next = parsed.getElementById('CartDrawer');
          if (current && next) {
            var wasOpen = current.classList.contains('is-open');
            current.innerHTML = next.innerHTML;
            current.drawer = new DrawerBase(current);
            if (wasOpen) {
              current.classList.add('is-open');
              current.setAttribute('aria-hidden', 'false');
            }
          }
        } else if (key === 'main-cart') {
          var currentCart = document.querySelector('[data-section-type="main-cart"]');
          var nextCart = parsed.querySelector('[data-section-type="main-cart"]');
          if (currentCart && nextCart) currentCart.innerHTML = nextCart.innerHTML;
        } else if (key === 'header') {
          var bubble = document.querySelector('[data-cart-icon-bubble]');
          var nextBubble = parsed.querySelector('[data-cart-icon-bubble]');
          if (bubble && nextBubble) bubble.innerHTML = nextBubble.innerHTML;
        }
      });
    },

    add: function (formData) {
      var sections = this.sectionsToRender();
      formData.append('sections', sections.join(','));
      formData.append('sections_url', window.location.pathname);

      return fetch(Theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          if (data.status) throw new Error(data.description || data.message);
          Cart.renderSections(data.sections);
          announce(Theme.strings.addToCart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
          return data;
        });
    },

    change: function (payload) {
      payload.sections = this.sectionsToRender().join(',');
      payload.sections_url = window.location.pathname;

      return fetch(Theme.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) { return res.json(); })
        .then(function (data) {
          Cart.renderSections(data.sections);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
          return data;
        });
    }
  };

  window.Cart = Cart;

  /* Quantity steppers and line item changes (event delegation, survives re-render) */
  document.addEventListener('click', function (event) {
    var down = event.target.closest('[data-qty-down]');
    var up = event.target.closest('[data-qty-up]');
    var remove = event.target.closest('[data-cart-remove]');

    if (down || up) {
      var wrap = (down || up).closest('[data-qty]');
      var input = wrap && wrap.querySelector('[data-qty-input]');
      if (!input) return;
      var min = parseInt(input.getAttribute('min') || '0', 10);
      var value = parseInt(input.value, 10) || 0;
      value = up ? value + 1 : Math.max(min, value - 1);
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (remove) {
      event.preventDefault();
      var key = remove.getAttribute('data-line-key');
      var container = remove.closest('[data-line-item]');
      if (container) container.classList.add('loading');
      Cart.change({ id: key, quantity: 0 }).catch(function () {
        if (container) container.classList.remove('loading');
      });
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target.closest('[data-qty-input][data-line-key]');
    if (!input) return;
    var container = input.closest('[data-line-item]');
    if (container) container.classList.add('loading');
    Cart.change({
      id: input.getAttribute('data-line-key'),
      quantity: parseInt(input.value, 10) || 0
    }).catch(function () {
      if (container) container.classList.remove('loading');
    });
  });

  /* ----------------------------------------------------------------------
     <product-form>
     ---------------------------------------------------------------------- */
  class ProductFormElement extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.button = this.querySelector('[name="add"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onSubmit(event) {
      if (this.form.querySelector('[name="id"]') === null) return;
      event.preventDefault();
      if (this.button && this.button.getAttribute('aria-disabled') === 'true') return;

      var self = this;
      var originalLabel = this.button ? this.button.textContent : '';
      if (this.button) {
        this.button.classList.add('loading');
        this.button.setAttribute('aria-disabled', 'true');
      }
      if (this.errorEl) this.errorEl.hidden = true;

      Cart.add(new FormData(this.form))
        .then(function () {
          var drawer = document.getElementById('CartDrawer');
          if (Theme.cartType === 'drawer' && drawer && typeof drawer.open === 'function') {
            drawer.open(self.button);
          } else if (Theme.cartType !== 'drawer') {
            window.location.href = Theme.routes.cart;
          }
        })
        .catch(function (error) {
          if (self.errorEl) {
            self.errorEl.textContent = error.message || Theme.strings.cartError;
            self.errorEl.hidden = false;
          }
        })
        .finally(function () {
          if (self.button) {
            self.button.classList.remove('loading');
            self.button.removeAttribute('aria-disabled');
            self.button.textContent = originalLabel;
          }
        });
    }
  }
  customElements.define('product-form', ProductFormElement);

  /* ----------------------------------------------------------------------
     <variant-selector>
     ---------------------------------------------------------------------- */
  class VariantSelectorElement extends HTMLElement {
    connectedCallback() {
      var data = this.querySelector('[data-variant-json]');
      if (!data) return;
      try {
        this.variants = JSON.parse(data.textContent);
      } catch (e) {
        return;
      }
      this.productUrl = this.getAttribute('data-url');
      this.addEventListener('change', this.onChange.bind(this));
      this.updateAvailability();
    }

    selectedOptions() {
      return Array.prototype.slice
        .call(this.querySelectorAll('[data-option-index]'))
        .filter(function (el) {
          return el.tagName === 'SELECT' || el.checked;
        })
        .sort(function (a, b) {
          return a.getAttribute('data-option-index') - b.getAttribute('data-option-index');
        })
        .map(function (el) { return el.value; });
    }

    onChange() {
      var chosen = this.selectedOptions();
      var match = this.variants.find(function (variant) {
        return variant.options.every(function (option, index) {
          return option === chosen[index];
        });
      });

      this.updateAvailability();
      this.render(match);
    }

    updateAvailability() {
      var chosen = this.selectedOptions();
      var variants = this.variants;
      this.querySelectorAll('.option-pill input[data-option-index]').forEach(function (input) {
        var index = parseInt(input.getAttribute('data-option-index'), 10);
        var probe = chosen.slice();
        probe[index] = input.value;
        var possible = variants.some(function (variant) {
          if (!variant.available) return false;
          return variant.options.every(function (option, i) {
            return i === index ? option === input.value : (probe[i] === undefined || option === probe[i]);
          });
        });
        input.disabled = !possible && !input.checked;
      });
    }

    render(variant) {
      var idInput = this.querySelector('[data-variant-id]');
      var button = document.querySelector('[data-add-button]');
      var priceEl = document.querySelector('[data-variant-price]');
      var stockEl = document.querySelector('[data-variant-stock]');
      var skuEl = document.querySelector('[data-variant-sku]');

      if (!variant) {
        if (button) {
          button.setAttribute('aria-disabled', 'true');
          button.textContent = Theme.strings.unavailable;
        }
        return;
      }

      if (idInput) idInput.value = variant.id;

      if (this.productUrl && window.history.replaceState) {
        window.history.replaceState({}, '', this.productUrl + '?variant=' + variant.id);
      }

      if (priceEl) {
        var html = '<span class="price__current">' + formatMoney(variant.price) + '</span>';
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          html =
            '<span class="price__sale">' + formatMoney(variant.price) + '</span>' +
            '<s class="price__regular">' + formatMoney(variant.compare_at_price) + '</s>';
        }
        priceEl.innerHTML = html;
        priceEl.classList.toggle('price--sale', !!(variant.compare_at_price && variant.compare_at_price > variant.price));
      }

      if (skuEl) skuEl.textContent = variant.sku || '';

      if (stockEl) {
        stockEl.classList.toggle('product__stock--in', variant.available);
        stockEl.classList.toggle('product__stock--out', !variant.available);
        stockEl.textContent = variant.available
          ? stockEl.getAttribute('data-in-stock')
          : stockEl.getAttribute('data-out-of-stock');
      }

      if (button) {
        if (variant.available) {
          button.removeAttribute('aria-disabled');
          button.textContent = Theme.strings.addToCart;
        } else {
          button.setAttribute('aria-disabled', 'true');
          button.textContent = Theme.strings.soldOut;
        }
      }

      if (variant.featured_media_id) {
        var gallery = document.querySelector('[data-gallery]');
        if (gallery) {
          var thumb = gallery.querySelector('[data-media-id="' + variant.featured_media_id + '"]');
          if (thumb) thumb.click();
        }
      }
    }
  }
  customElements.define('variant-selector', VariantSelectorElement);

  /* ----------------------------------------------------------------------
     <product-gallery>
     ---------------------------------------------------------------------- */
  class ProductGalleryElement extends HTMLElement {
    connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      var self = this;
      this.querySelectorAll('[data-media-id]').forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          self.querySelectorAll('[data-media-id]').forEach(function (el) {
            el.setAttribute('aria-current', 'false');
          });
          thumb.setAttribute('aria-current', 'true');
          var target = self.querySelector('[data-media-slide="' + thumb.getAttribute('data-media-id') + '"]');
          if (target && self.main) {
            self.main.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
          }
        });
      });
    }
  }
  customElements.define('product-gallery', ProductGalleryElement);

  /* ----------------------------------------------------------------------
     <whatsapp-form>
     Builds a wa.me deep link from the form values and opens it.
     The underlying markup is a real Shopify contact form, so it still works
     with JavaScript disabled and can optionally email a copy to the shop.
     ---------------------------------------------------------------------- */
  class WhatsAppFormElement extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.number = (this.getAttribute('data-number') || '').replace(/[^0-9]/g, '');
      this.alsoEmail = this.getAttribute('data-also-email') === 'true';
      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.autoFillTopic();
    }

    autoFillTopic() {
      try {
        var params = new URLSearchParams(window.location.search);
        var targetType = params.get('type') || params.get('topic') || params.get('enquiry') || params.get('subject');
        if (!targetType) return;

        var topicSelect = this.form.querySelector('[data-field="topic"]') || this.form.querySelector('select[name*="[Enquiry type]"]') || this.form.querySelector('select[name*="[topic]"]');
        if (!topicSelect) return;

        var cleanTarget = targetType.toLowerCase().replace(/[^a-z0-9]/g, '');
        var matchedIndex = -1;

        for (var i = 0; i < topicSelect.options.length; i++) {
          var opt = topicSelect.options[i];
          var optClean = (opt.value || opt.text || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          if (optClean === cleanTarget) {
            matchedIndex = i;
            break;
          }
        }

        if (matchedIndex === -1) {
          for (var j = 0; j < topicSelect.options.length; j++) {
            var opt2 = topicSelect.options[j];
            var optClean2 = (opt2.value || opt2.text || '').toLowerCase().replace(/[^a-z0-9]/g, '');
            if (optClean2 && (optClean2.includes(cleanTarget) || cleanTarget.includes(optClean2))) {
              matchedIndex = j;
              break;
            }
          }
        }

        if (matchedIndex !== -1) {
          topicSelect.selectedIndex = matchedIndex;
          topicSelect.dispatchEvent(new Event('change', { bubbles: true }));
        }
      } catch (e) {}
    }

    value(name) {
      var field = this.form.querySelector('[data-field="' + name + '"]');
      return field ? field.value.trim() : '';
    }

    onSubmit(event) {
      if (!this.form.checkValidity()) return; // let the browser show its messages

      event.preventDefault();

      if (!this.number) {
        this.form.submit();
        return;
      }

      var lines = [];
      var intro = this.getAttribute('data-intro');
      if (intro) lines.push(intro);

      var topic = this.value('topic');
      var name = this.value('name');
      var message = this.value('message');

      if (topic) lines.push('Enquiry: ' + topic);
      if (name) lines.push('Name: ' + name);
      if (message) lines.push('', message);

      var url = 'https://wa.me/' + this.number + '?text=' + encodeURIComponent(lines.join('\n'));
      var opened = window.open(url, '_blank', 'noopener');
      if (!opened) {
        window.location.href = url;
      }

      if (this.alsoEmail) {
        try {
          fetch(this.form.action || '/contact', {
            method: 'POST',
            body: new FormData(this.form)
          });
        } catch (e) {}
      }
    }
  }
  customElements.define('whatsapp-form', WhatsAppFormElement);

  // Global enquiry auto-fill for any standard contact form
  document.addEventListener('DOMContentLoaded', function () {
    try {
      var params = new URLSearchParams(window.location.search);
      var targetType = params.get('type') || params.get('topic') || params.get('enquiry') || params.get('subject');
      if (!targetType) return;

      var selects = document.querySelectorAll('select[data-field="topic"], select[name*="[Enquiry type]"], select[name*="[topic]"]');
      selects.forEach(function (select) {
        var cleanTarget = targetType.toLowerCase().replace(/[^a-z0-9]/g, '');
        for (var i = 0; i < select.options.length; i++) {
          var val = (select.options[i].value || select.options[i].text || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          if (val === cleanTarget || (val && (val.includes(cleanTarget) || cleanTarget.includes(val)))) {
            select.selectedIndex = i;
            select.dispatchEvent(new Event('change', { bubbles: true }));
            break;
          }
        }
      });
    } catch (e) {}
  });

  /* ----------------------------------------------------------------------
     Header behaviour
     ---------------------------------------------------------------------- */
  var header = document.querySelector('[data-header]');
  if (header) {
    var setHeaderHeight = function () {
      document.documentElement.style.setProperty('--header-height', header.offsetHeight + 'px');
    };
    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight);

    if (header.hasAttribute('data-header-transparent')) {
      var onScroll = function () {
        header.classList.toggle('header--bordered', window.scrollY > 12);
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /* ----------------------------------------------------------------------
     Scroll reveal
     ---------------------------------------------------------------------- */
  if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );

    var observeReveals = function () {
      document.querySelectorAll('.reveal:not(.is-visible)').forEach(function (el, index) {
        el.style.transitionDelay = Math.min(index % 4, 3) * 70 + 'ms';
        observer.observe(el);
      });
    };
    observeReveals();
    document.addEventListener('shopify:section:load', observeReveals);
  } else {
    document.querySelectorAll('.reveal').forEach(function (el) { el.classList.add('is-visible'); });
  }

  /* ----------------------------------------------------------------------
     Category pill filter & Frontend sorting
     ---------------------------------------------------------------------- */
  function initCollectionFiltersAndSort() {
    var sections = document.querySelectorAll('[data-section-type="main-collection"]');
    if (!sections.length) return;

    sections.forEach(function (section) {
      var container = section.querySelector('[data-category-filter]');
      var sortSelect = section.querySelector('[data-sort-select]');
      var grid = section.querySelector('[data-products-grid]');
      var countEl = section.querySelector('[data-product-count]');
      if (!grid) return;

      var items = Array.prototype.slice.call(grid.querySelectorAll('.product-grid-item, [data-collections], [data-category]'));
      if (!items.length) return;

      var buttons = container ? container.querySelectorAll('[data-filter]') : [];
      var currentFilter = 'all';
      var currentSort = sortSelect ? sortSelect.value : 'manual';

      function sortItems(sortKey) {
        currentSort = sortKey;
        var sorted = items.slice().sort(function (a, b) {
          switch (sortKey) {
            case 'price-ascending':
              return (parseFloat(a.getAttribute('data-price')) || 0) - (parseFloat(b.getAttribute('data-price')) || 0);
            case 'price-descending':
              return (parseFloat(b.getAttribute('data-price')) || 0) - (parseFloat(a.getAttribute('data-price')) || 0);
            case 'title-ascending':
              return (a.getAttribute('data-title') || '').localeCompare(b.getAttribute('data-title') || '', undefined, { sensitivity: 'base' });
            case 'title-descending':
              return (b.getAttribute('data-title') || '').localeCompare(a.getAttribute('data-title') || '', undefined, { sensitivity: 'base' });
            case 'created-descending':
              return (parseInt(b.getAttribute('data-created'), 10) || 0) - (parseInt(a.getAttribute('data-created'), 10) || 0);
            case 'created-ascending':
              return (parseInt(a.getAttribute('data-created'), 10) || 0) - (parseInt(b.getAttribute('data-created'), 10) || 0);
            case 'manual':
            case 'best-selling':
            default:
              return (parseInt(a.getAttribute('data-index'), 10) || 0) - (parseInt(b.getAttribute('data-index'), 10) || 0);
          }
        });

        sorted.forEach(function (item) {
          grid.appendChild(item);
        });
      }

      function applyFilter(filterKey, updateUrl) {
        currentFilter = filterKey;
        if (buttons.length) {
          buttons.forEach(function (btn) {
            btn.classList.toggle('is-active', btn.getAttribute('data-filter') === filterKey);
          });
        }

        var visibleCount = 0;
        items.forEach(function (item) {
          var colString = (item.getAttribute('data-collections') || item.getAttribute('data-category') || '').toLowerCase();
          var cols = colString.split(/\s+/);
          if (filterKey === 'all' || cols.indexOf(filterKey) !== -1 || colString.indexOf(filterKey) !== -1) {
            item.style.display = '';
            item.classList.add('is-visible');
            visibleCount++;
          } else {
            item.style.display = 'none';
            item.classList.remove('is-visible');
          }
        });

        if (countEl) {
          countEl.textContent = visibleCount + ' ' + (visibleCount === 1 ? 'product' : 'products');
        }

        if (updateUrl !== false) {
          syncUrl();
        }
      }

      function syncUrl() {
        try {
          var url = new URL(window.location);
          if (currentFilter === 'all') {
            url.searchParams.delete('category');
          } else {
            url.searchParams.set('category', currentFilter);
          }

          if (currentSort && currentSort !== 'manual') {
            url.searchParams.set('sort_by', currentSort);
          } else {
            url.searchParams.delete('sort_by');
          }

          window.history.replaceState({}, '', url);
        } catch (e) {}
      }

      if (buttons.length) {
        buttons.forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            applyFilter(btn.getAttribute('data-filter'), true);
          });
        });
      }

      if (sortSelect) {
        sortSelect.addEventListener('change', function () {
          sortItems(sortSelect.value);
          syncUrl();
        });
      }

      // Initial state from URL
      try {
        var urlParams = new URLSearchParams(window.location.search);
        var initialCat = urlParams.get('category');
        var initialSort = urlParams.get('sort_by');

        if (initialSort && sortSelect) {
          var matchingOpt = sortSelect.querySelector('option[value="' + initialSort + '"]');
          if (matchingOpt) {
            sortSelect.value = initialSort;
            sortItems(initialSort);
          }
        }

        if (initialCat && container && container.querySelector('[data-filter="' + initialCat + '"]')) {
          applyFilter(initialCat, false);
        }
      } catch (e) {}
    });
  }

  initCollectionFiltersAndSort();
  document.addEventListener('shopify:section:load', initCollectionFiltersAndSort);

  /* ----------------------------------------------------------------------
     Theme editor support
     ---------------------------------------------------------------------- */
  document.addEventListener('shopify:section:load', function (event) {
    var drawer = event.target.querySelector('cart-drawer, menu-drawer');
    if (drawer && typeof drawer.connectedCallback === 'function') drawer.connectedCallback();
  });
})();
